/**
 * EvaluationForm 编辑模块 - 渲染函数
 */

import React from "react";
import type { ColumnsType } from "antd/es/table";
import type { RatingType } from "./types";
import { type FlatRatingRecord } from "../../hooks/useRatingData";
import {
  RATING_TYPE_CONFIG,
  ASSESSMENT_STATUS_STYLE,
  STATUS_DOT_SIZE,
} from "./constants";

// ==================== 评估状态徽章渲染 ====================

/**
 * 渲染评估状态徽章（已评估/未评估）
 * @param assessed - 是否已评估
 * @returns JSX 元素
 */
export const renderAssessmentStatusBadge = (assessed: boolean) => {
  const style = assessed
    ? ASSESSMENT_STATUS_STYLE.ASSESSED
    : ASSESSMENT_STATUS_STYLE.NOT_ASSESSED;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "88px",
        height: "32px",
        gap: "4px",
        borderRadius: "3px",
        fontSize: "14px",
        fontWeight: 500,
        backgroundColor: style.backgroundColor,
        color: style.color,
      }}
    >
      <span
        style={{
          ...STATUS_DOT_SIZE,
          borderRadius: "50%",
          backgroundColor: style.dotColor,
          flexShrink: 0,
        }}
      />
      {assessed ? "已评估" : "未评估"}
    </span>
  );
};

// ==================== 评级数据表格列配置 ====================

/**
 * 生成评级数据表格列配置
 * 根据展平后的数据动态计算行合并
 *
 * @param flatData - 展平后的表格数据
 * @param ratingType - 评级类型
 * @returns 表格列配置
 */
export const getRatingDataTableColumns = (
  flatData: FlatRatingRecord[],
  ratingType: RatingType = "excellent",
): ColumnsType<FlatRatingRecord> => {
  return [
    {
      title: "评估阶段",
      dataIndex: "stageName",
      key: "stageName",
      width: 150,
      align: "center",
    },
    {
      title: "评估维度",
      dataIndex: "dimensionName",
      key: "dimensionName",
      width: 150,
      align: "center",
    },
    {
      title: "评估事项",
      dataIndex: "itemName",
      key: "itemName",
      width: 200,
      align: "left",
    },
    {
      title: "说明",
      dataIndex: "description",
      key: "description",
      width: 200,
      align: "left",
    },
  ];
};

// ==================== Phase 标题渲染 ====================

/**
 * 渲染 Phase 标题和评估人信息
 */
export const renderPhaseHeader = (
  phaseName: string,
  purchaseUserName: string,
  excellentNum?: number,
  middleNum?: number,
  poorNum?: number,
) => (
  <div
    style={{
      fontSize: "14px",
      fontWeight: 600,
      padding: "12px 0",
      borderBottom: "1px solid #f0f0f0",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <span>{phaseName}</span>
      <div
        style={{
          width: "1px",
          height: "16px",
          backgroundColor: "#d9d9d9",
          flexShrink: 0,
        }}
      />
      <span style={{ fontSize: "14px", fontWeight: 400, color: "#000000" }}>
        评估人：{purchaseUserName}
      </span>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span>【</span>
      <span style={{ fontSize: "14px", fontWeight: 400, color: "#000000" }}>
        优：
        <span style={{ color: "#52c41a", fontWeight: 600 }}>
          {excellentNum ?? 0}
        </span>
      </span>
      <span style={{ fontSize: "14px", fontWeight: 400, color: "#000000" }}>
        中：
        <span style={{ color: "#faad14", fontWeight: 600 }}>
          {middleNum ?? 0}
        </span>
      </span>
      <span style={{ fontSize: "14px", fontWeight: 400, color: "#000000" }}>
        差：
        <span style={{ color: "#ff4d4f", fontWeight: 600 }}>
          {poorNum ?? 0}
        </span>
      </span>
      <span>】</span>
    </div>
  </div>
);

// ==================== 数据解析错误提示 ====================

/**
 * 渲染数据解析失败提示
 */
export const renderParseErrorPlaceholder = (error: Error) => (
  <div
    style={{
      color: "#999",
      textAlign: "center",
      padding: "20px",
      backgroundColor: "#fafafa",
    }}
  >
    <div>数据解析失败</div>
    <div style={{ fontSize: "12px", marginTop: "8px", color: "#ccc" }}>
      错误: {error.message}
    </div>
  </div>
);

/**
 * 渲染无数据提示
 */
export const renderEmptyPlaceholder = () => (
  <div
    style={{
      color: "#999",
      textAlign: "center",
      padding: "20px",
      backgroundColor: "#fafafa",
    }}
  >
    暂无数据
  </div>
);

// ==================== 转换后数据渲染 ====================

/**
 * 渲染转换后的评估数据信息
 * 用于日志输出和调试
 */
export const renderEvaluationDataInfo = (
  phaseIdStr: string,
  evaluationDataArray: any[],
) => {};

// ==================== 评级类型标题渲染 ====================

/**
 * 渲染评级类型的标题
 */
export const renderRatingTypeTitle = (ratingType: RatingType, styles: any) => {
  const className =
    ratingType === "excellent"
      ? styles.excellentReviewTitle
      : ratingType === "middle"
        ? styles.midTitle
        : ratingType === "poor"
          ? styles.poorTitle
          : "";

  return (
    <div className={className}>{RATING_TYPE_CONFIG[ratingType].label}</div>
  );
};
